import { Features, isFeatureEnabled } from '../../../../featureToggles/features';
import { AccessTypes } from '../../../common/domain/accountAccessType';

const isTrialsOfTiersEnabled = isFeatureEnabled(Features.TrialsOfTiers);

export enum PricingDetailsIcon {
  Check = 'check',
  Minus = 'minus'
}

export type PricingComparisonValuesType = {
  name: string;
  lite: string | PricingDetailsIcon;
  professional: string | PricingDetailsIcon;
  enterprise: string | PricingDetailsIcon;
  hasTooltip?: boolean;
};

export interface IPricingDetails {
  title: string;
  values: PricingComparisonValuesType[];
  isCoreFeature?: boolean;
}

export const accountTypeNames = {
  [AccessTypes.Enterprise]: isTrialsOfTiersEnabled ? 'Enterprise' : 'SightX Enterprise',
  [AccessTypes.Lite]: isTrialsOfTiersEnabled ? 'Lite' : 'SightX Lite',
  [AccessTypes.Professional]: isTrialsOfTiersEnabled ? 'Professional' : 'SightX Professional'
};

const proUpgradeText = isTrialsOfTiersEnabled ? 'Upgrade to Pro' : 'Upgrade to Pro  🚀';
const enterpriseUpgradeText = isTrialsOfTiersEnabled ? 'Contact Sales' : 'Contact sales';

export const plansInfo = {
  [AccessTypes.Enterprise]: {
    name: 'Enterprise',
    users: 'Unlimited users',
    description: 'Everything in Professional, plus...',
    features: ['Best-in-class enterprise solution', 'Advanced research methodologies', 'Dedicated customer success', 'Powerful APIs and integrations'],
    upgradeText: enterpriseUpgradeText
  },
  [AccessTypes.Professional]: {
    name: 'Professional',
    users: 'Up to three users',
    description: 'Everything in Lite, plus...',
    features: ['All-in-one research suite', 'Advanced automated analytics', 'Access to millions of global consumers', 'Team collaboration'],
    upgradeText: proUpgradeText
  }
};

export const plansCardDetails = {
  [AccessTypes.Enterprise]: {
    name: 'Enterprise',
    description: 'Custom quote to fit your needs',
    upgradeText: enterpriseUpgradeText
  },
  [AccessTypes.Professional]: {
    name: 'Professional',
    description: '$135 per month per user<br/>Billed annually',
    upgradeText: proUpgradeText
  },
  [AccessTypes.Lite]: {
    name: 'Lite',
    description: 'FREE forever',
    upgradeText: ''
  }
};

export const pricingDetails: IPricingDetails[] = [
  {
    isCoreFeature: true,
    title: 'Core Features',
    values: [
      {
        name: 'Surveys',
        lite: 'Unlimited',
        professional: 'Unlimited',
        enterprise: 'Unlimited'
      },
      {
        name: 'Responses',
        lite: 'Up to 500',
        professional: 'Up to 40k per year',
        enterprise: 'Unlimited'
      },
      {
        name: 'Users',
        lite: 'Single user',
        professional: 'Up to three users',
        enterprise: 'Unlimited'
      }
    ]
  },
  {
    title: 'Survey Capabilities',
    values: [
      { name: 'Basic question types', lite: PricingDetailsIcon.Check, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Survey logic', lite: PricingDetailsIcon.Check, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Conditional display', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Quotas', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Answer piping and carry-forward', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Automated data cleaning', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Custom logos and colors', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Survey completion redirect', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Embedded variables', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Minus, enterprise: PricingDetailsIcon.Check },
      { name: 'Page randomization', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Minus, enterprise: PricingDetailsIcon.Check },
      { name: 'Language translation', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Minus, enterprise: PricingDetailsIcon.Check }
    ]
  },
  {
    title: 'Survey Distribution Methods',
    values: [
      { name: 'Survey link', lite: PricingDetailsIcon.Check, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'SightX Audiences', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check, hasTooltip: true },
      { name: 'QR code', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Email', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'SMS', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check }
    ]
  },
  {
    title: 'Research Methodologies',
    values: [
      { name: 'Concept testing', lite: PricingDetailsIcon.Minus, professional: 'Basic', enterprise: 'Advanced' },
      { name: 'Heatmaps', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'MaxDiff', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Minus, enterprise: PricingDetailsIcon.Check },
      { name: 'Conjoint', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Minus, enterprise: PricingDetailsIcon.Check },
      { name: 'Van Westendorp', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Minus, enterprise: PricingDetailsIcon.Check },
      { name: 'Gabor-Granger', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Minus, enterprise: PricingDetailsIcon.Check },
      { name: 'Key driver analysis', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Minus, enterprise: PricingDetailsIcon.Check }
    ]
  },
  {
    title: 'Analytics',
    values: [
      { name: 'Real-time question analysis', lite: PricingDetailsIcon.Check, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Data filters and comparisons', lite: PricingDetailsIcon.Check, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Top/bottom box configuration', lite: PricingDetailsIcon.Check, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Sentiment analysis', lite: PricingDetailsIcon.Check, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Word clouds', lite: PricingDetailsIcon.Check, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Data exports', lite: 'Raw data only', professional: 'Excel, Powerpoint, SPSS', enterprise: 'Excel, Powerpoint, SPSS' },
      { name: 'Customer segmentation', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Significance testing', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'Custom data tables & crosstabs', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Check, enterprise: PricingDetailsIcon.Check },
      { name: 'TURF analysis', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Minus, enterprise: PricingDetailsIcon.Check },
      { name: 'Automated segmentation', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Minus, enterprise: PricingDetailsIcon.Check },
      { name: 'Data weighting', lite: PricingDetailsIcon.Minus, professional: PricingDetailsIcon.Minus, enterprise: PricingDetailsIcon.Check }
    ]
  },
  {
    title: 'Advanced Capabilities',
    values: [
      {
        name: 'Platform support',
        lite: 'In-app',
        professional: 'Email and in-app',
        enterprise: 'Dedicated customer success'
      },
      {
        name: 'Research services',
        lite: PricingDetailsIcon.Minus,
        professional: 'Additional fee',
        enterprise: 'Additional fee'
      },
      {
        name: 'Unlimited API access',
        lite: PricingDetailsIcon.Minus,
        professional: PricingDetailsIcon.Minus,
        enterprise: PricingDetailsIcon.Check
      },
      {
        name: 'Single sign-on (SSO)',
        lite: PricingDetailsIcon.Minus,
        professional: PricingDetailsIcon.Minus,
        enterprise: PricingDetailsIcon.Check
      },
      {
        name: 'Local server hosting',
        lite: PricingDetailsIcon.Minus,
        professional: PricingDetailsIcon.Minus,
        enterprise: PricingDetailsIcon.Check
      }
    ]
  }
];

export const MAX_ALLOWED_SEATS_FOR_PRO = 3;
